import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const LINE_FALLBACK_PX = 24;
const INITIAL_LINES = 5;
const AUTO_SCROLL_EDGE_PX = 44;
const AUTO_SCROLL_SPEED_PX = 10;
const RECT_INSET_PX = 8; // selection rect's horizontal inset within the transcript column

const formatDuration = (seconds) => {
  const total = Math.max(0, Math.round(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

// A blue outline overlaid on the transcript. It snaps to text lines (line-height grid anchored
// per row, so it never drifts), while the clip's time range is taken from every paragraph the
// outline touches — a partial line still pulls in the whole paragraph. Rows are read straight
// from the DOM (one `[id^="event-"]` per filtered body, same order) so nothing has to thread
// through the memoized TranscriptRow.
const ClipLineSelector = ({ active, preparing, bodies, onCancel, onConfirm }) => {
  const rootRef = useRef(null);
  const dragRef = useRef(null);
  const scrollParentRef = useRef(null);
  const autoScrollRef = useRef(null);
  const [sel, setSel] = useState(null); // { top, bottom } px within the transcript list
  const selRef = useRef(sel);
  selRef.current = sel;
  // Viewport box of the selection. The dim + the interactive rect are portaled to <body> and
  // positioned from this, so the overhanging handles paint above the dim instead of under it.
  const [dimClip, setDimClip] = useState(null);

  const rows = () => Array.from(rootRef.current?.parentElement?.querySelectorAll('[id^="event-"]') ?? []);
  const originTop = () => rootRef.current?.getBoundingClientRect().top ?? 0;
  const contentHeight = () => rootRef.current?.offsetHeight ?? 0;

  const lineHeight = () => {
    const p = rootRef.current?.parentElement?.querySelector('.parliament-live-event p.paragraph-p1');
    return (p && parseFloat(getComputedStyle(p).lineHeight)) || LINE_FALLBACK_PX;
  };

  const rowRanges = () => {
    const top0 = originTop();
    return rows().map((row) => {
      const box = row.getBoundingClientRect();
      return { top: box.top - top0, bottom: box.bottom - top0 };
    });
  };

  // Snap y to the nearest line boundary of whichever row it lands in — keeps the grid aligned to
  // real text lines instead of accumulating offset over the paragraph gaps.
  const snapToLine = (y) => {
    const grid = lineHeight();
    const ranges = rowRanges();
    const row =
      ranges.find((r) => y >= r.top - grid && y <= r.bottom + grid) ??
      (y <= (ranges[0]?.top ?? 0) ? ranges[0] : ranges[ranges.length - 1]);
    if (!row) return Math.round(y / grid) * grid;
    const snapped = row.top + Math.round((y - row.top) / grid) * grid;
    return Math.max(row.top, Math.min(snapped, row.bottom));
  };

  const coveredItems = (range) => {
    if (!range) return [];
    return rowRanges().reduce((acc, r, i) => {
      if (r.bottom > range.top && r.top < range.bottom) acc.push(bodies[i]);
      return acc;
    }, []);
  };

  const stopAutoScroll = () => {
    if (autoScrollRef.current) cancelAnimationFrame(autoScrollRef.current);
    autoScrollRef.current = null;
  };

  // The in-flow root scrolls with the transcript; project the current selection onto the viewport
  // through it, so the portaled dim + rect track it.
  const syncDimClip = () => {
    const root = rootRef.current;
    const s = selRef.current;
    if (!root || !s) return setDimClip(null);
    const o = root.getBoundingClientRect();
    setDimClip({
      top: Math.round(o.top + s.top),
      left: Math.round(o.left + RECT_INSET_PX),
      right: Math.round(o.right - RECT_INSET_PX),
      bottom: Math.round(o.top + s.bottom),
    });
  };

  // Open centred in the viewport so both handles are on screen.
  useLayoutEffect(() => {
    if (!active) {
      setSel(null);
      setDimClip(null);
      return;
    }
    if (!rows().length) return;
    const height = lineHeight() * INITIAL_LINES;
    const centre = window.innerHeight / 2 - originTop() - height / 2;
    const top = Math.max(0, Math.min(snapToLine(centre), contentHeight() - height));
    setSel({ top, bottom: top + height });
  }, [active]);

  useLayoutEffect(() => {
    if (active) syncDimClip();
  }, [sel, active]);

  useEffect(() => {
    if (!active) return;
    let raf = null;
    const onViewChange = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        syncDimClip();
      });
    };
    window.addEventListener('scroll', onViewChange, true);
    window.addEventListener('resize', onViewChange);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      stopAutoScroll();
      document.body.style.cursor = '';
      window.removeEventListener('scroll', onViewChange, true);
      window.removeEventListener('resize', onViewChange);
    };
  }, [active]);

  const beginDrag = (mode) => (e) => {
    if (preparing) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    document.body.style.cursor = mode === 'move' ? 'grabbing' : 'ns-resize';
    scrollParentRef.current = rootRef.current?.closest('.custom-scrollbars__content') ?? null;
    // originTop0 lets applyDrag fold in any auto-scroll that happens mid-drag.
    dragRef.current = {
      mode,
      startY: e.clientY,
      lastY: e.clientY,
      top: sel.top,
      bottom: sel.bottom,
      originTop0: originTop(),
    };
  };

  const applyDrag = (clientY) => {
    const drag = dragRef.current;
    if (!drag) return;
    const grid = lineHeight();
    const maxY = contentHeight();
    const delta = clientY - drag.startY + (drag.originTop0 - originTop());
    let next;
    if (drag.mode === 'top') {
      const top = Math.max(0, Math.min(snapToLine(drag.top + delta), drag.bottom - grid));
      next = { top, bottom: drag.bottom };
    } else if (drag.mode === 'bottom') {
      const bottom = Math.min(maxY, Math.max(snapToLine(drag.bottom + delta), drag.top + grid));
      next = { top: drag.top, bottom };
    } else {
      const height = drag.bottom - drag.top;
      const top = Math.max(0, Math.min(snapToLine(drag.top + delta), maxY - height));
      next = { top, bottom: top + height };
    }
    setSel(next);
  };

  // Keep scrolling the transcript while the pointer sits in the top/bottom edge zone, re-running
  // the drag math each frame so the selection follows the newly revealed lines.
  const autoScrollStep = () => {
    const drag = dragRef.current;
    const scroller = scrollParentRef.current;
    if (!drag || !scroller) return stopAutoScroll();
    const box = scroller.getBoundingClientRect();
    const dir = drag.lastY < box.top + AUTO_SCROLL_EDGE_PX ? -1 : drag.lastY > box.bottom - AUTO_SCROLL_EDGE_PX ? 1 : 0;
    if (dir === 0) return stopAutoScroll();
    const before = scroller.scrollTop;
    scroller.scrollTop = before + dir * AUTO_SCROLL_SPEED_PX;
    if (scroller.scrollTop !== before) {
      applyDrag(drag.lastY);
      syncDimClip();
    }
    autoScrollRef.current = requestAnimationFrame(autoScrollStep);
  };

  const endDrag = () => {
    document.body.style.cursor = '';
    dragRef.current = null;
    stopAutoScroll();
  };

  const onMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    // Pointer capture can be lost (window leave, cancelled gesture) without a pointerup; without
    // this a plain hover would keep resizing the selection.
    if (e.buttons === 0) return endDrag();
    drag.lastY = e.clientY;
    applyDrag(e.clientY);
    if (!autoScrollRef.current) autoScrollRef.current = requestAnimationFrame(autoScrollStep);
  };

  if (!active || !bodies.length) return null;

  const selected = coveredItems(sel);
  const first = selected[0];
  const last = selected[selected.length - 1];
  const durationSeconds =
    first && last ? (last.dateTimeTimestamp ?? 0) + (last.duration ?? 0) - (first.dateTimeTimestamp ?? 0) : 0;

  const dimBox = dimClip && {
    top: dimClip.top,
    left: dimClip.left,
    width: dimClip.right - dimClip.left,
    height: dimClip.bottom - dimClip.top,
  };

  return (
    <>
      <div ref={rootRef} className='clip-line-selector'>
        <div className='clip-line-selector-catcher' onPointerDown={onCancel} />
      </div>
      {createPortal(
        <>
          {dimBox && (
            <>
              <div className='clip-line-selector-dim' style={dimBox} />
              <div
                className='clip-line-selector-rect'
                style={dimBox}
                onPointerMove={onMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
              >
                <button
                  type='button'
                  className='clip-line-selector-handle clip-line-selector-handle-top'
                  aria-label='Adjust clip start'
                  onPointerDown={beginDrag('top')}
                />
                <div className='clip-line-selector-body' onPointerDown={beginDrag('move')} />
                <button
                  type='button'
                  className='clip-line-selector-handle clip-line-selector-handle-bottom'
                  aria-label='Adjust clip end'
                  onPointerDown={beginDrag('bottom')}
                />
              </div>
            </>
          )}
          <div className='clip-line-selector-toolbar flex-centered justify-content-between rounded-pill specific-y-padding-5 specific-x-padding-8'>
            <div className='flex-centered'>
              <span className='rounded-pill bg-main-blue text-white specific-y-padding-5 px-4'>
                Duration {formatDuration(durationSeconds)}
              </span>
            </div>
            <div className='flex-centered'>
              <button type='button' className='general-button text-main-blue mr-2' onClick={onCancel}>
                Cancel
              </button>
              <button
                type='button'
                className='general-button action-button rounded-pill px-4 specific-y-padding-5 '
                disabled={preparing || !selected.length}
                onClick={() => onConfirm(selected)}
              >
                {preparing ? <span className='fas fa-spinner fa-spin' /> : 'Create clip'}
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
};

export default ClipLineSelector;
